import React, { useState, useRef, useEffect } from "react";
import logo from "../../assets/images/logo.svg";
import "./navBar.scss";
import { Link } from "react-router-dom";
import { useLanguage } from "../../LanguageContext";

const NavBar = () => {
  const { t, lang, setLang } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Close dropdown when clicking outside and handle window resize
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle language dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      
      // Handle mobile menu
      if (isMenuOpen && menuRef.current && menuButtonRef.current && 
          !menuRef.current.contains(event.target) && 
          !menuButtonRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    const handleResize = () => {
      // Update language dropdown position if open
      if (isDropdownOpen) {
        const button = dropdownRef.current?.querySelector('.lang-toggle');
        if (button) {
          const rect = button.getBoundingClientRect();
          const dropdown = dropdownRef.current?.querySelector('.lang-dropdown');
          if (dropdown) {
            dropdown.style.top = `${rect.bottom + window.scrollY}px`;
            dropdown.style.left = `${rect.right - 80}px`;
          }
        }
      }
      
      // Close mobile menu when resizing to desktop
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isDropdownOpen, isMenuOpen]);

  const handleLanguageChange = (newLang) => {
    setLang(newLang);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    if (!isDropdownOpen) {
      // Calculate position when opening dropdown
      const button = dropdownRef.current?.querySelector('.lang-toggle');
      if (button) {
        const rect = button.getBoundingClientRect();
        const dropdown = dropdownRef.current?.querySelector('.lang-dropdown');
        if (dropdown) {
          dropdown.style.top = `${rect.bottom + window.scrollY}px`;
          dropdown.style.left = `${rect.right - 80}px`; // 80px is min-width
        }
      }
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    if (window.innerWidth < 768) {
      setIsMenuOpen(false);
    }
  };

  // Navigation items
  const navItems = [
    { to: "/about", text: t("nav_about"), className: "" },
    { to: "/services", text: t("nav_services"), className: "services" },
    { to: "/contact", text: t("nav_contact"), className: "contact" },
  ];

  return (
    <header>
      <nav>
        <div className="logo">
          <Link to="/" onClick={closeMenu}>
            <img src={logo} alt="logo" />
          </Link>
          <button 
            ref={menuButtonRef}
            className="menu-toggle" 
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>
        </div>

        <div className={`menu ${isMenuOpen ? 'active' : ''}`} ref={menuRef}>
          <ul>
            {navItems.map((item, index) => (
              <li key={index}>
                <Link to={item.to} onClick={closeMenu}>
                  <button className={item.className}>
                    {item.text}
                  </button>
                </Link>
              </li>
            ))}

            <li>
              <div className="lang-switch" ref={dropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className="lang-toggle"
                >
                  {lang.toUpperCase()}
                  <span className="dropdown-arrow">▼</span>
                </button>
                {isDropdownOpen && (
                  <div className="lang-dropdown">
                    <button
                      onClick={() => handleLanguageChange("en")}
                      className={lang === "en" ? "active" : ""}
                    >
                      EN
                    </button>
                    <button
                      onClick={() => handleLanguageChange("es")}
                      className={lang === "es" ? "active" : ""}
                    >
                      ES
                    </button>
                  </div>
                )}
              </div>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default NavBar;
